import { AxiosError } from "axios";

import { getUserInternalAccessToken } from "@/api/access-token-backoffice";
import { buildPaginationQuery } from "@/helpers/request-helpers";
import { IPromptAI } from "@/interfaces/prompt-ai.interface";
import { apiBackofficeT2b } from "@/lib/axios";
import { IFilter } from "@/types/filter.types";
import { PaginatedResponse, RequestPagination } from "@/types/requests.types";

const baseUrl = "/v1/promptai/filter";

function normalizePromptAI(raw: Record<string, unknown>): IPromptAI | null {
  const idRaw = raw.id ?? raw.Id;
  const id = typeof idRaw === "number" ? idRaw : Number(idRaw);

  if (!Number.isFinite(id)) return null;

  return {
    id,
    friendlyName: String(raw.friendlyName ?? raw.FriendlyName ?? ""),
    description: String(raw.description ?? raw.Description ?? ""),
    content: String(raw.content ?? raw.Content ?? ""),
    rowDate: String(raw.rowDate ?? raw.RowDate ?? ""),
  };
}

export async function filterPromptAI(
  filter: IFilter<IPromptAI>[],
  pagination: RequestPagination
): Promise<PaginatedResponse<IPromptAI>> {
  try {
    const { accessToken } = await getUserInternalAccessToken();
    const paginationQuery = buildPaginationQuery(pagination);

    const response = await apiBackofficeT2b.post(
      `${baseUrl}?${paginationQuery}`,
      { filter },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    const data = response.data as Record<string, unknown> & {
      items?: Record<string, unknown>[];
      Items?: Record<string, unknown>[];
    };

    const rawItems = data.items ?? data.Items ?? [];
    const items = rawItems
      .map((item) => normalizePromptAI(item as Record<string, unknown>))
      .filter((item): item is IPromptAI => item !== null);

    return {
      items,
      totalCount: Number(data.totalCount ?? data.TotalCount ?? items.length),
      pageNumber: Number(data.pageNumber ?? data.PageNumber ?? pagination.pageNumber),
      pageSize: Number(data.pageSize ?? data.PageSize ?? pagination.pageSize),
      hasNextPage: Boolean(data.hasNextPage ?? data.HasNextPage ?? false),
      hasPreviousPage: Boolean(data.hasPreviousPage ?? data.HasPreviousPage ?? false),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || "Failed to fetch PromptAI");
    }

    throw new Error("Failed to fetch PromptAI");
  }
}
